import React, { useEffect, useState, useCallback, useRef } from 'react';
import './default.scss';
import { Card, CardBody, Spinner } from '@material-tailwind/react';
import ProgressBar from '../../commonComp/ProgressBar';
import { useNavigate } from 'react-router-dom';
import { getApiResponse } from '../../APIs/getApiResponse';
import { apiBaseUrl, endpoints } from '../../APIs/endpoints';
import DatePicker from '../../commonComp/DateRangePicker';
import { handleError } from '../../APIs/apiUtils';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import axios from 'axios';

// Utility function to format date (No changes needed)
const formatDate = (date) => {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
};

// Function to get last week's date range (No changes needed)
const getLastWeekDateRange = () => {
    const today = new Date();
    const lastWeekStart = new Date(today.getFullYear(), today.getMonth(), today.getDate() - today.getDay() - 6);
    const lastWeekEnd = new Date(today.getFullYear(), today.getMonth(), today.getDate() - today.getDay());
    return {
        startDate: formatDate(lastWeekStart),
        endDate: formatDate(lastWeekEnd)
    };
};

const Index = () => {
    const navigate = useNavigate();
    const [dashboardData, setDashboardData] = useState({});
    const [dateRange, setDateRange] = useState(getLastWeekDateRange());
    const [isLoading, setIsLoading] = useState(true);
    const [isRedirecting, setIsRedirecting] = useState(true);
    const [calendarDates, setCalendarDates] = useState(new Set());
    const effectRan = useRef(false);

    // Effect for finding and redirecting to the nearest date (No changes needed)
    useEffect(() => {

        console.log("Effect ran");
        const LOCAL_STORAGE_START_DATE_KEY = 'globalStartDate';
        const LOCAL_STORAGE_END_DATE_KEY = 'globalEndDate';
        localStorage.removeItem(LOCAL_STORAGE_START_DATE_KEY);
        localStorage.removeItem(LOCAL_STORAGE_END_DATE_KEY);

        if (effectRan.current === true) {
            return;
        }

        const findAndRedirectToNearestDate = async () => {
            try {
                if (localStorage.getItem('access_token') === null) {
                    navigate("/login");
                    return;
                }
                const schoolname = localStorage.getItem("school_name");
                if (!schoolname) {
                    setIsRedirecting(false);
                    return;
                }
                const attendanceCalendarResponse = await axios.get(
                    `${apiBaseUrl}/api/attendance/calendar?school=${encodeURIComponent(schoolname)}`
                );
                const datesToHighlight = Object.keys(attendanceCalendarResponse.data);
                
                localStorage.setItem('calendarDates', JSON.stringify(datesToHighlight));
                setCalendarDates(new Set(datesToHighlight));
                console.log("Dates" , calendarDates)

                if (datesToHighlight.length === 0) {
                    setIsRedirecting(false);
                    return;
                }

                const today = new Date();
                today.setHours(0, 0, 0, 0);

                let nearestDateObj = null;
                let minTimeDiff = Infinity;

                datesToHighlight.forEach((dateString) => {
                    const [year, month, day] = dateString.split("-").map(Number);
                    const dateObj = new Date(year, month - 1, day);
                    dateObj.setHours(0, 0, 0, 0);
                    const timeDiff = Math.abs(dateObj.getTime() - today.getTime());
                    if (timeDiff < minTimeDiff) {
                        minTimeDiff = timeDiff;
                        nearestDateObj = dateObj;
                    }
                });

                if (nearestDateObj) {
                    const day = String(nearestDateObj.getDate()).padStart(2, '0');
                    const month = String(nearestDateObj.getMonth() + 1).padStart(2, '0');
                    const year = nearestDateObj.getFullYear();
                    const ddmmyyyy = `${day}-${month}-${year}`;
                    navigate(`/dashboard/${ddmmyyyy}`);
                } else {
                    setIsRedirecting(false);
                }
            } catch (error) {
                console.error("Failed to fetch calendar data for redirection:", error);
                setIsRedirecting(false);
                handleError(error);
            }
        };

        findAndRedirectToNearestDate();
        return () => {
            effectRan.current = true;
        };
    }, [navigate]);

    const getDashboardData = useCallback(async (fromDate, toDate) => {
        setIsLoading(true);
        try {
            if (localStorage.getItem('access_token') === null) {
                navigate("/login");
                return;
            }
            const urlParams = `fromDate=${fromDate}&toDate=${toDate}`;
            const resp = await getApiResponse(`${endpoints.DEFAULT_DASHBOARD}?${urlParams}`);
            if (resp.status === 200) {
                setDashboardData(resp.data || {});
            } else {
                handleError({ response: { status: resp.status } });
            }
        } catch (error) {
            const { errorMessage, statusCode } = handleError(error);
            if (statusCode === 401) navigate("/login");
            else navigate('/error', { state: { errorMessage } });
        } finally {
            setIsLoading(false);
        }
    }, [navigate]);

    const getDateRange = useCallback((range) => {
        setDateRange(range);
    }, []);

    useEffect(() => {
        if (!isRedirecting && dateRange.startDate && dateRange.endDate) {
            getDashboardData(dateRange.startDate, dateRange.endDate);
        }
    }, [dateRange, getDashboardData, isRedirecting]);

    const MultiProgressBar = ({ width = [] }) => {
        const progressColors = ['#00BB61', '#42EA5D', '#FFE28F', '#FFA3A3', '#D75252'];
        const validWidths = width.filter(w => w && w !== '0%');
        return (
            <div className="mb-6 md:mb-8"> 
                <div className="flex-start flex h-1.5 w-full rounded-full bg-blue-gray-50 font-sans text-xs font-medium">
                    {validWidths.map((w, i) => (
                        <div key={i} style={{ width: w, backgroundColor: progressColors[i] }} className="flex h-full items-center justify-center rounded-full text-white">
                            {w !== '0%' && <div className="mt-7 text-black text-nowrap text-xs sm:text-sm">{w}</div>}
                        </div>
                    ))}
                </div>
            </div>
        );
    };
    
    // Card component with responsive styling
    const card = (data, color = 'green') => {
        const { name, attention_span = '0%', motivation = '0%', attendance, emotional_state } = data;
        if (attendance === '0/0') return null;
        return (
            <div className='class-info-card' onClick={() => navigate(`/behaviour/${name}`, { state: { fromDate: dateRange.startDate, toDate: dateRange.endDate } })}>
                <Card style={{ border: `1px solid ${color}` }}>
                    {/* Responsive padding for the card body */}
                    <CardBody className="p-3 sm:p-4">
                        <div className='class-header mb-2'>
                            {/* Responsive text size and padding for class number */}
                            <div className='class-number text-base sm:text-lg md:text-xl p-2 sm:p-3' style={{ background: color }}>{name}</div>
                        </div>
                        <div className='class-progress-details'>
                            <div className='progress-bar-section'>
                                {/* Responsive text size for progress titles */}
                                <div className='progress-title text-sm sm:text-base'><span>Attention Span</span><span>{attention_span}</span></div>
                                <ProgressBar width={attention_span} color={color} />
                            </div>
                            <div className='progress-bar-section'>
                                <div className='progress-title text-sm sm:text-base'><span>Motivation & Initiation</span><span>{motivation}</span></div>
                                <ProgressBar width={motivation} color={color} />
                            </div>
                            <div className='progress-bar-section'>
                                <div className='progress-title text-sm sm:text-base'><span>Emotional State</span><span className='emoji-icons'><span>😊</span>-<span>😡</span></span></div>
                                <MultiProgressBar width={emotional_state} />
                            </div>
                        </div>
                        <div className='card-footer-content'>
                            <div className='card-footer-info'>
                                {/* Responsive text sizes for footer info */}
                                <div className="text-lg sm:text-xl md:text-2xl font-semibold">{attendance}</div>
                                <div className="text-xs sm:text-sm text-gray-600">Attendance</div>
                            </div>
                        </div>
                    </CardBody>
                </Card>
            </div>
        );
    };

    const colors = ['#FF9595', '#AEBD00', '#C62CFD', '#FF9595', '#1808CB', '#9CC0F5'];

    if (isRedirecting) {
        return (
            <div className="flex justify-center items-center h-screen">
                {/* Responsive spinner size */}
                <Spinner className="h-12 w-12 md:h-16 md:w-16" color="blue" />
            </div>
        );
    }
    
    return (
        // Responsive padding for the main container
        <div className='default-dashboard-container p-3 sm:p-4 md:p-6'>
            <ToastContainer />
            {/* Responsive margin for the filters section */}
            <div className='dashboard-filters mb-4 md:mb-6'>
                <div className='search-date w-full sm:w-auto'>
                    <DatePicker 
                        getDateRange={getDateRange} 
                        initialDateRange={dateRange}
                        initiallySelected={true}
                        highlightedDatesSet={calendarDates}
                    />
                </div>
            </div>
            {isLoading ? (
                <div className="flex justify-center items-start h-screen pt-10">
                     {/* Responsive spinner size */}
                    <Spinner className="h-10 w-10 md:h-12 md:w-12" color="blue" />
                </div>
            ) : (
                <div className='default-content-section mt-4'>
                    <div className='default-left-section'>
                         {/* Responsive text size and margin for the section title */}
                        <h3 className='section-title text-xl sm:text-2xl md:text-3xl mb-4 md:mb-6'>All Classes</h3>
                        {/* Responsive grid for the cards list with adjusted gaps */}
                        <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6'>
                            {dashboardData?.classes?.map((cardObj, i) => card(cardObj, colors[i])).filter(Boolean)}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Index;